import { intlLocale, type Locale, type NumeralSystem } from "./i18n";

/**
 * Dates, always in somebody's timezone rather than in the browser's.
 *
 * The timezone is a required argument, not an optional one, and that is the
 * whole design. A warehouse in Beirut has staff opening the console from a
 * phone abroad, and `new Date().toLocaleString()` renders a shift that started
 * at 08:00 as 06:00 for them — a wrong answer that looks like a right one. The
 * person's timezone is on `/me/context` (EP00-S03) precisely so the client can
 * render every timestamp in it, and making it a parameter means a call site
 * cannot quietly fall back to the machine's.
 *
 * Latin digits, in Arabic as in English, for the same reason money uses them:
 * a delivery slot read off a printed manifest and a delivery slot on a screen
 * should be comparable at a glance.
 *
 * Times come from the API as RFC 3339 strings. They are parsed here rather than
 * at each call site so that an unparseable one fails in one place.
 */

/** A moment, as the API sends it or as JavaScript holds it. */
export type Instant = string | number | Date;

/** What an unreadable timestamp renders as. An em dash, which reads as "no value". */
const ABSENT = "—";

function toDate(value: Instant): Date | null {
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
}

export interface DateOptions {
	locale: Locale;
	/** An IANA name, from `preferences.timezone`. */
	timeZone: string;
	numerals?: NumeralSystem;
}

/**
 * The region whose date conventions each locale follows.
 *
 * `en` maps to `en-GB`, and that is a deliberate choice rather than a default
 * left unexamined. Bare `en` resolves to US conventions, so `13 Sep 2026`
 * renders as `Sep 13, 2026` — month first, which is the minority order
 * worldwide and not the one anybody in Lebanon reads. The language stays
 * English; only the field order changes.
 *
 * It applies to dates alone. Money keeps the plain locale, because `en-GB`
 * would render US dollars as `US$1,250.50` — disambiguating a currency nobody
 * here is confused about, at the cost of two characters on every price.
 */
const DATE_REGION: Record<Locale, string> = { en: "en-GB", ar: "ar" };

function dateLocale(locale: Locale, numerals: NumeralSystem): string {
	return `${DATE_REGION[locale]}-u-nu-${numerals}`;
}

function formatter(
	{ locale, timeZone, numerals = "latn" }: DateOptions,
	options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
	try {
		return new Intl.DateTimeFormat(dateLocale(locale, numerals), { ...options, timeZone });
	} catch {
		// An unloadable zone. The server validates them on write, so this is a
		// row from an older build or a browser missing the data — and a date in
		// the wrong zone still beats a screen that will not render.
		return new Intl.DateTimeFormat(dateLocale(locale, numerals), options);
	}
}

/** A calendar date: `13 Sep 2026`. */
export function formatDate(value: Instant, options: DateOptions): string {
	const date = toDate(value);
	if (date === null) {
		return ABSENT;
	}
	return formatter(options, { day: "numeric", month: "short", year: "numeric" }).format(date);
}

/** A date and a time: `13 Sep 2026, 09:00`. */
export function formatDateTime(value: Instant, options: DateOptions): string {
	const date = toDate(value);
	if (date === null) {
		return ABSENT;
	}
	return formatter(options, {
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		// 24-hour in both locales. An operations console is full of shift times
		// and delivery windows, and "8:00" without an am/pm beside it is the one
		// ambiguity a warehouse cannot afford.
		hour12: false,
	}).format(date);
}

/** Just the time: `09:00`. */
export function formatTime(value: Instant, options: DateOptions): string {
	const date = toDate(value);
	if (date === null) {
		return ABSENT;
	}
	return formatter(options, { hour: "2-digit", minute: "2-digit", hour12: false }).format(date);
}

/** The thresholds a relative time is bucketed into, largest first. */
const UNITS: { unit: Intl.RelativeTimeFormatUnit; ms: number }[] = [
	{ unit: "year", ms: 365 * 24 * 60 * 60 * 1000 },
	{ unit: "month", ms: 30 * 24 * 60 * 60 * 1000 },
	{ unit: "week", ms: 7 * 24 * 60 * 60 * 1000 },
	{ unit: "day", ms: 24 * 60 * 60 * 1000 },
	{ unit: "hour", ms: 60 * 60 * 1000 },
	{ unit: "minute", ms: 60 * 1000 },
];

/**
 * How long ago, in words: `3 hours ago`, `in 2 days`.
 *
 * For a log or an activity feed, where "when did this happen" matters more than
 * the exact clock time. Everything a person might act on — a delivery window, a
 * shift, an invoice date — uses {@link formatDateTime} instead, because "2 days
 * ago" is not something you can check against a paper manifest.
 *
 * `now` is a parameter so a test does not have to mock the clock, and so a
 * table can render fifty rows against one timestamp rather than reading the
 * clock fifty times and getting fifty slightly different answers.
 */
export function formatRelative(value: Instant, options: DateOptions & { now?: Instant }): string {
	const date = toDate(value);
	const reference = toDate(options.now ?? Date.now());
	if (date === null || reference === null) {
		return ABSENT;
	}

	const delta = date.getTime() - reference.getTime();
	const { locale, numerals = "latn" } = options;
	const format = new Intl.RelativeTimeFormat(intlLocale(locale, numerals), { numeric: "auto" });

	for (const { unit, ms } of UNITS) {
		if (Math.abs(delta) >= ms) {
			// Truncated towards zero rather than rounded, so something 89 minutes
			// old reads "1 hour ago" and never "2 hours ago" — a relative time
			// that overstates the age is worse than one that understates it.
			return format.format(Math.trunc(delta / ms), unit);
		}
	}
	// Under a minute. `numeric: "auto"` renders this as "now" rather than
	// "in 0 seconds".
	return format.format(0, "second");
}
